import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import * as THREE from 'three'
import useGateBoundingDataChecker from "../../utils/gateBoundingDataChecker"

const BramaHoleGate = forwardRef(({ gate, onUpdate, textures, ...props }, ref) => {
  const group = useRef(null)
  const meshRef = useRef(null)

  useImperativeHandle(ref, () => group.current)

  // cm -> m
  const holeWidth = Number(gate?.width ?? 0) / 100
  const holeHeight = Number(gate?.height ?? 0) / 100

  // grubość klocka do wycięcia ściany (musi przejść przez wall)
  const holeDepth = 0.6

  const geometry = useMemo(() => {
    return new THREE.BoxGeometry(holeDepth, holeHeight, holeWidth)
  }, [holeDepth, holeHeight, holeWidth])

  const material = useMemo(() => {
    // DEBUG – żebyś widział gdzie jest (zakomentuj 2 linie i odkomentuj 2 linie niżej)
    const debugColor = '#00ff66' // <- zakomentuj
    const debugOpacity = 0.25    // <- zakomentuj

    const m = new THREE.MeshBasicMaterial({
      color: debugColor,
      transparent: true,
      // opacity: debugOpacity,

      opacity: 0,        // <- odkomentuj żeby było niewidzialne
    })

    m.depthWrite = false
    // m.colorWrite = false // <- odkomentuj żeby było niewidzialne
    return m
  }, [])

  /**
   * ✅ Najważniejsze:
   * - nodes dajemy “sztuczne”, ale stabilne i zależne od:
   *   1) geometrii (zmiana width/height)
   *   2) referencji gate (parent może podmieniać gate i zerować boundingData na null)
   * Dzięki temu hook odpali callback i zawsze uzupełni bounding.
   */
  const nodes = useMemo(() => ({ HoleGate: { geometry, gate } }), [geometry, gate])

  useGateBoundingDataChecker({
    nodes,
    gate,
    group,
    onBoundingDataChanged: () => {
      if (!meshRef.current) return
      if (typeof onUpdate !== 'function') return

      // macierze world muszą być aktualne
      group.current?.updateWorldMatrix(true, true)
      meshRef.current.updateWorldMatrix(true, true)

      const box = new THREE.Box3().setFromObject(meshRef.current)
      const size = new THREE.Vector3()
      box.getSize(size)

      const x = (-Number(props?.length ?? 0) / 100) / 2
      const y = 0
      const z = gate?.position?.z ?? 0
      const adjustedY = y + holeHeight / 2

      onUpdate({
        boundingData: { box, size },
        closedBoundingBox: { box: box.clone(), size: size.clone() },
        positionAdjusted: [x, adjustedY, z],
      })
    },
  })

  return (
    <group
      ref={group}
      wall="front_wall"
      dispose={null}
      position={[gate?.position?.x ?? 0, gate?.position?.y ?? 0, gate?.position?.z ?? 0]}
      castShadow={false}
      receiveShadow={false}
    >
      <mesh
        ref={meshRef}
        geometry={geometry}
        material={material}
        // stało na ziemi
        position={[0, holeHeight / 2, 0]}
        frustumCulled={false}
      />
    </group>
  )
})

export default BramaHoleGate
